import React, { forwardRef } from 'react';
import { transparentize } from '../../../Constants/Colors';
import { AnimationTimings } from '../../../Constants/Animation';
import { Scheme, Size, Variant } from '../../../Constants/System';
import Color from 'color';
export function createButtonElement(component, displayName) {
    const Button = forwardRef((props, ref) => {
        const elementProps = getButtonElementProps(props, ref);
        return React.createElement(component, elementProps);
    });
    Button.displayName = displayName;
    return Button;
}
export function getButtonElementProps(props, ref) {
    const styleProps = {
        size: props.size ? props.size : Size.medium,
        scheme: props.scheme ? props.scheme : Scheme.primary,
        disabled: props.disabled ? props.disabled : false,
        variant: props.variant,
        ctaGradient: props.ctaGradient,
    };
    return {
        ...props,
        ref: ref,
        $buttonProps: styleProps,
    };
}
const HeightMap = {
    [Size.small]: '32px',
    [Size.medium]: '40px',
    [Size.large]: '48px',
};
const PaddingMap = {
    [Size.small]: '7px 10px',
    [Size.medium]: '10px 13px',
    [Size.large]: '14px 17px',
};
export const getOutline = (scheme, theme) => {
    if (scheme == Scheme.primary) {
        return theme.secondary;
    }
    else {
        return theme.schemes[scheme].passive;
    }
};
export const getBaseButtonStyles = (props) => {
    return `
		display: inline-block;
		cursor: pointer;
		border: none;
		background: ${props.scheme.passive};
		color: ${props.scheme.text};
		min-height: ${HeightMap[props.size]};
		padding: ${PaddingMap[props.size]};
		transition: background-color 200ms ${AnimationTimings.productive};
		transition: box-shadow 200ms ${AnimationTimings.productive};

		${!props.disabled
        ? `
				&:hover {
					background: ${props.scheme.hover};
				}

				&:active {
					background: ${props.scheme.active};
				}
		
				&:focus {
					box-shadow: inset 0px 0px 0px 2px ${props.theme.palette.brand1};
				}
			`
        : `
				filter: opacity(0.5);
				cursor: not-allowed;
			`}
	`;
};
export const getButtonStyles = (styleProps, theme) => {
    const isDarkTheme = Color(theme.palette.primary).isDark();
    let scheme = theme.schemes[styleProps.scheme];
    switch (styleProps.variant) {
        case Variant.outline:
            scheme = isDarkTheme
                ? {
                    ...theme.schemes[Scheme.secondary],
                    hover: transparentize(theme.primary, 0.8),
                }
                : {
                    ...theme.schemes[Scheme.primary],
                    hover: transparentize(theme.secondary, 0.9),
                };
            return `
				${getBaseButtonStyles({
                scheme,
                theme,
                size: styleProps.size,
                disabled: styleProps.disabled,
            })}
				background: transparent;
				box-shadow: inset 0px 0px 0px 2px ${getOutline(styleProps.scheme, theme)};
				color: ${theme.secondary};
			`;
        case Variant.cta:
            // eslint-disable-next-line no-case-declarations
            const gradient = styleProps.ctaGradient ? theme.gradients[styleProps.ctaGradient] : theme.gradients.flare;
            return `
				${getBaseButtonStyles({
                scheme: theme.schemes[styleProps.scheme],
                theme: theme,
                size: styleProps.size,
                disabled: styleProps.disabled,
            })}
				background: linear-gradient(to right, ${gradient.from} 0%, ${gradient.to} 100%);
				color: ${theme.secondary};

				&:hover {
					background: linear-gradient(to right, ${gradient.from} 40%, ${gradient.to} 100%);
					box-shadow: 0px 5px 15px ${gradient.from};
					color: ${theme.secondary};
				}

				&:active {
					background: linear-gradient(to right, ${gradient.from} 60%, ${gradient.to} 100%);
					box-shadow: 0px 5px 25px ${gradient.from};
					color: ${theme.secondary};
				}

				transition: background 600ms ${AnimationTimings.productive};
				transition: box-shadow 0.2s ${AnimationTimings.productive};
			`;
        case Variant.quiet:
            return `
				${getBaseButtonStyles({
                scheme: theme.schemes[Scheme.primary],
                theme,
                size: styleProps.size,
                disabled: styleProps.disabled,
            })}
				background: transparent;
				color: ${theme.schemes.brand1.passive};
			`;
        case Variant.solid:
        default:
            return getBaseButtonStyles({
                scheme: theme.schemes[styleProps.scheme],
                theme: theme,
                size: styleProps.size,
                disabled: styleProps.disabled,
            });
    }
};
//# sourceMappingURL=ButtonHelpers.js.map